import { RowClassParams, RowClickedEvent, RowStyle } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useSetAtom } from 'jotai';
import { Button } from 'primereact/button';
import { useCallback, useMemo, useRef, useState } from 'react';
import { selectWidgetAIAtom } from '~/atoms/dashboardAtom';
import { agTheme } from '~/components/ui/tables/ag/agTheme';
import { usePopout } from '~/contexts/PopoutContext';
import {
  ColumnConfig,
  generateAgGridColumns,
} from '~/lib/table/generateAgGridColumns';
import { WidgetActions } from '../../common/WidgetActions';
export type { ColumnConfig };

export type DynamicTableWidgetProps<
  T extends Record<string, unknown> = Record<string, unknown>,
> = {
  columns: ColumnConfig[];
  data: T[];
  refetch: () => void;
  handleRemoveWidget?: (widgetId: string) => void;
  widgetId: string;
  title?: string;
  formatters?: Record<string, (value: unknown, data: T) => unknown>;
  callbacks?: Record<string, (row: T) => void>;
  shortDescription?: string;
};

export const DynamicTableWidget = <
  T extends Record<string, unknown> = Record<string, unknown>,
>({
  columns,
  data,
  refetch,
  handleRemoveWidget,
  widgetId,
  title = 'Dynamic Table',
  formatters = {},
  callbacks = {},
  shortDescription,
}: DynamicTableWidgetProps<T>) => {
  const [selectedRowId, setSelectedRowId] = useState<string | number | null>(
    null,
  );
  const gridRef = useRef<AgGridReact<T>>(null);

  const setSelectWidgetAI = useSetAtom(selectWidgetAIAtom);
  const { openPopout } = usePopout();

  const getRowStyle = useCallback(
    (params: RowClassParams<T, unknown>) => {
      if (
        selectedRowId &&
        params.data &&
        'id' in params.data &&
        (params.data as Record<string, unknown>)['id'] === selectedRowId
      ) {
        return { backgroundColor: 'rgba(0, 0, 0, 0.4)' } as RowStyle;
      }
      return undefined;
    },
    [selectedRowId],
  );

  const removeWidget = useCallback(
    () => handleRemoveWidget?.(widgetId),
    [widgetId, handleRemoveWidget],
  );

  const onRowClicked = (event: RowClickedEvent<T>) => {
    setSelectedRowId((event.data as { id?: string | number })?.id ?? null);
  };

  const columnDefs = useMemo(
    () => generateAgGridColumns(columns, { formatters, callbacks }),
    [columns, formatters, callbacks],
  );

  const handleAIClick = useCallback(() => {
    setSelectWidgetAI(widgetId);
  }, [setSelectWidgetAI, widgetId]);

  const handlePopout = useCallback(() => {
    openPopout(
      widgetId,
      {
        type: 'DynamicTableWidget',
        props: {
          columns,
          data,
          refetch,
          handleRemoveWidget,
          widgetId,
          title,
          formatters,
          callbacks,
          shortDescription,
        },
      },
      {
        width: 1200,
        height: 700,
        title: shortDescription ?? title,
      },
    );
  }, [
    columns,
    data,
    refetch,
    handleRemoveWidget,
    widgetId,
    title,
    formatters,
    callbacks,
    shortDescription,
    openPopout,
  ]);

  return (
    <div className="hover-container h-full" id={widgetId}>
      <div className="flex justify-content-between align-items-center">
        <div className="w-full flex justify-content-between align-items-center">
          <div className="flex align-items-center gap-3">
            <h3 className="mb-0">{shortDescription ?? title}</h3>
          </div>
          <div className="align-items-center flex flex-row align-self-start">
            <Button
              icon="pi pi-microchip-ai"
              color="blue-500"
              className="cursor-pointer hover-icon"
              text
              onClick={handleAIClick}
            />
            <WidgetActions
              onRemove={removeWidget}
              onRefresh={() => refetch()}
              onAddToChat={setSelectWidgetAI}
              onPopout={handlePopout}
              widgetId={widgetId}
            />
          </div>
        </div>
      </div>
      <div
        className="mt-3"
        style={{ height: 'calc(100% - 3.4rem)', marginLeft: '0' }}
      >
        <AgGridReact<T>
          rowData={data}
          ref={gridRef}
          columnDefs={columnDefs}
          tooltipShowDelay={0}
          suppressCellFocus
          onGridReady={() => gridRef.current?.api.sizeColumnsToFit()}
          autoSizeStrategy={{
            type: 'fitGridWidth',
            defaultMinWidth: 100,
          }}
          getRowStyle={getRowStyle}
          onRowClicked={onRowClicked}
          theme={agTheme}
          loadThemeGoogleFonts={true}
        />
      </div>
    </div>
  );
};
